"use client";

import React, { useMemo, useState, useContext, useCallback, useEffect, useRef } from "react";
import Script from "next/script";

import { DEFAULT_LANG_OBJ, weglotScriptSrc } from "@stadium/services/weglot/constants";
import { translateWord, translateWords } from "@stadium/services/weglot/helpers";
import { logWeglotError } from "@stadium/services/weglot/log-error";
import {
  buildWeglotLanguageList,
  getLanguageListKey,
} from "@stadium/services/weglot/language-list";

const defaultContextValue = {
  initialize: () => {},
  loaded: false,
  loadFailed: false,
  instance: null,
  initialized: false,
  currentLang: DEFAULT_LANG_OBJ.key,
  availableLanguages: [],
  languageListKey: "",
  switchLanguage: () => {},
  getTranslation: async ({ text }) => text,
  batchGetTranslation: async ({ texts, from: _from, to: _to }) => texts,
};

export const WeglotContext = React.createContext(defaultContextValue);

export const useWeglot = () => useContext(WeglotContext);

const WeglotProvider = ({ children, apiKey, enabled = true }) => {
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [instance, setInstance] = useState(null);
  const [initialized, setInitialized] = useState(!enabled);
  const [availableLanguages, setAvailableLanguages] = useState(() =>
    enabled ? [] : [DEFAULT_LANG_OBJ],
  );
  const [languageListKey, setLanguageListKey] = useState(() =>
    enabled ? "" : DEFAULT_LANG_OBJ.key,
  );
  const [currentLang, setCurrentLang] = useState(DEFAULT_LANG_OBJ.key);
  const initStartedRef = useRef(false);
  const unavailableRef = useRef(false);

  const markUnavailable = useCallback((message, { error, extra } = {}) => {
    if (unavailableRef.current) return;

    unavailableRef.current = true;
    logWeglotError(message, { error, extra });
    setLoadFailed(true);
    setAvailableLanguages([DEFAULT_LANG_OBJ]);
    setLanguageListKey(DEFAULT_LANG_OBJ.key);
    setCurrentLang(DEFAULT_LANG_OBJ.key);
    setInitialized(true);
  }, []);

  const onLoad = () => {
    setLoaded(true);
  };

  const onScriptError = (error) => {
    markUnavailable("Weglot script failed to load", {
      error: error instanceof Error ? error : undefined,
      extra: { phase: "script-load", scriptSrc: weglotScriptSrc },
    });
  };

  const initialize = useCallback(() => {
    if (!enabled || !loaded || !apiKey || initStartedRef.current || loadFailed) return;

    const weglot = globalThis?.Weglot;
    if (!weglot?.initialize) {
      markUnavailable("Weglot script loaded but global is unavailable", {
        extra: { phase: "initialize", scriptSrc: weglotScriptSrc },
      });
      return;
    }

    initStartedRef.current = true;

    try {
      weglot.initialize({ api_key: apiKey, hide_switcher: true, cache: true });
      setInstance(weglot);
    } catch (error) {
      initStartedRef.current = false;
      markUnavailable("Weglot initialize failed", {
        error,
        extra: { phase: "initialize" },
      });
    }
  }, [enabled, loaded, apiKey, loadFailed, markUnavailable]);

  useEffect(() => {
    if (!enabled || apiKey) return;

    markUnavailable("Weglot API key missing", {
      extra: { phase: "config" },
    });
  }, [enabled, apiKey, markUnavailable]);

  useEffect(() => {
    if (!enabled || !instance) return undefined;

    let cancelled = false;

    const hydrateFromInstance = () => {
      if (cancelled || unavailableRef.current) return;

      const languages = buildWeglotLanguageList(instance.options, {
        getLanguageName: (languageTo) => instance.getLanguageName?.(languageTo),
      });

      if (!languages?.length) {
        logWeglotError("Weglot returned no languages", { extra: { phase: "hydrate" } });
        setAvailableLanguages([DEFAULT_LANG_OBJ]);
        setLanguageListKey(DEFAULT_LANG_OBJ.key);
        setInitialized(true);
        return;
      }

      const nextKey = getLanguageListKey(languages);
      setLanguageListKey((prevKey) => (prevKey === nextKey ? prevKey : nextKey));
      setAvailableLanguages((prev) => {
        if (getLanguageListKey(prev) === nextKey) return prev;
        return languages;
      });
      setInitialized(true);
    };

    if (instance.initialized) {
      hydrateFromInstance();
    } else {
      instance.on?.("initialized", hydrateFromInstance);

      queueMicrotask(() => {
        if (cancelled) return;

        const hasLanguages = instance.options?.languages?.some(
          ({ enabled, deleted_at }) => enabled && !deleted_at,
        );
        if (hasLanguages && !unavailableRef.current) {
          hydrateFromInstance();
        }
      });
    }

    return () => {
      cancelled = true;
      instance.off?.("initialized", hydrateFromInstance);
    };
  }, [enabled, instance]);

  useEffect(() => {
    if (!enabled || !instance) return undefined;

    const syncCurrentLang = () => {
      setCurrentLang(instance.getCurrentLang?.() ?? DEFAULT_LANG_OBJ.key);
    };

    syncCurrentLang();
    instance.on?.("languageChanged", syncCurrentLang);

    return () => {
      instance.off?.("languageChanged", syncCurrentLang);
    };
  }, [enabled, instance]);

  const switchLanguage = useCallback(
    (lang) => {
      if (!enabled || !instance || !lang) return;

      instance.switchTo?.(lang);
      // Weglot's languageChanged event is not always emitted synchronously after
      // switchTo, so update context immediately for dependent UI (e.g. country names).
      setCurrentLang(lang);
    },
    [enabled, instance],
  );

  const getTranslation = useCallback(
    async ({ text, lang: initialLang, from = "", to = "" }) => {
      if (!enabled || !instance) return text;

      const languageFrom = from || instance.getCurrentLang?.() || DEFAULT_LANG_OBJ.key;
      const languageTo = to || initialLang || DEFAULT_LANG_OBJ.key;
      if (languageFrom === languageTo) {
        return text;
      }

      const translatedWord = await translateWord({
        text,
        apiKey,
        from: languageFrom,
        to: languageTo,
      });
      return translatedWord || text;
    },
    [enabled, apiKey, instance],
  );

  const batchGetTranslation = useCallback(
    async ({ texts, from = "", to = "" }) => {
      if (!enabled || !instance) return texts;

      const languageFrom = from || instance.getCurrentLang?.() || DEFAULT_LANG_OBJ.key;
      const languageTo = to || DEFAULT_LANG_OBJ.key;
      if (languageFrom === languageTo) return texts;

      return translateWords({ texts, apiKey, from: languageFrom, to: languageTo });
    },
    [enabled, apiKey, instance],
  );

  const value = useMemo(
    () => ({
      initialize,
      loaded: enabled ? loaded : false,
      loadFailed,
      instance: enabled ? instance : null,
      initialized,
      currentLang: enabled ? currentLang : DEFAULT_LANG_OBJ.key,
      availableLanguages,
      languageListKey,
      switchLanguage,
      getTranslation,
      batchGetTranslation,
    }),
    [
      enabled,
      initialize,
      loaded,
      loadFailed,
      instance,
      initialized,
      currentLang,
      availableLanguages,
      languageListKey,
      switchLanguage,
      getTranslation,
      batchGetTranslation,
    ],
  );

  return (
    <>
      {enabled ? (
        <Script
          type="text/javascript"
          id="weglot-script"
          onLoad={onLoad}
          onError={onScriptError}
          src={weglotScriptSrc}
          strategy="lazyOnload"
        />
      ) : null}
      <WeglotContext.Provider value={value}>{children}</WeglotContext.Provider>
    </>
  );
};

export default WeglotProvider;
